/**
 * Import masivo de posts desde .html / .md / JSON preparados por IA.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, FileUp, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useUpsertBlogPostsBatch } from './hooks/useBlogAdmin';
import type { BlogBatchResult } from './types';
import {
    parseBlogPostJsonBatch,
    parseBlogPostText,
    parsedToBatchItem,
    type ParsedBlogFile,
} from './lib/parseBlogPostFile';
import { BLOG_AI_SYSTEM_PROMPT, BLOG_MD_TEMPLATE } from './lib/blogPostTemplate';
import { cn } from '@/lib/utils';

function errMsg(e: unknown): string {
    if (e instanceof Error) return e.message;
    if (e && typeof e === 'object' && 'message' in e) return String((e as { message: string }).message);
    return String(e);
}

function parseFiles(files: Array<{ name: string; text: string }>): ParsedBlogFile[] {
    const out: ParsedBlogFile[] = [];
    for (const file of files) {
        if (file.name.toLowerCase().endsWith('.json')) {
            try {
                out.push(...parseBlogPostJsonBatch(file.name, file.text));
            } catch (e) {
                out.push({
                    filename: file.name,
                    slug: '',
                    patch: { slug: '', title: '', body_html: '' },
                    warnings: [],
                    leak: false,
                    error: errMsg(e),
                });
            }
        } else {
            out.push(parseBlogPostText(file.name, file.text));
        }
    }
    return out;
}

export function BlogPostsImportPage() {
    const navigate = useNavigate();
    const mut = useUpsertBlogPostsBatch();
    const [parsed, setParsed] = useState<ParsedBlogFile[]>([]);
    const [paste, setPaste] = useState('');
    const [result, setResult] = useState<BlogBatchResult | null>(null);
    const [copied, setCopied] = useState<'prompt' | 'tpl' | null>(null);
    const [error, setError] = useState<string | null>(null);

    const ready = useMemo(
        () => parsed.filter((p) => p.slug && p.patch.title && p.patch.body_html && !p.error && !p.leak),
        [parsed],
    );
    const blocked = parsed.length - ready.length;

    const onFiles = async (list: FileList | null) => {
        if (!list?.length) return;
        setError(null);
        setResult(null);
        const files: Array<{ name: string; text: string }> = [];
        for (const file of Array.from(list)) {
            files.push({ name: file.name, text: await file.text() });
        }
        setParsed(parseFiles(files));
    };

    const onPasteParse = () => {
        if (!paste.trim()) return;
        setResult(null);
        const looksJson = paste.trim().startsWith('{') || paste.trim().startsWith('[');
        setParsed(parseFiles([{
            name: looksJson ? 'pegado.json' : 'pegado.html',
            text: paste,
        }]));
    };

    const downloadTemplate = () => {
        const blob = new Blob([BLOG_MD_TEMPLATE], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla-articulo-blog.html';
        a.click();
        URL.revokeObjectURL(url);
    };

    const copy = async (which: 'prompt' | 'tpl') => {
        await navigator.clipboard.writeText(which === 'prompt' ? BLOG_AI_SYSTEM_PROMPT : BLOG_MD_TEMPLATE);
        setCopied(which);
        window.setTimeout(() => setCopied(null), 2000);
    };

    const run = async (dryRun: boolean) => {
        setError(null);
        if (ready.length === 0) {
            setError('No hay artículos válidos para enviar');
            return;
        }
        try {
            const data = await mut.mutateAsync({
                dryRun,
                items: ready.map(parsedToBatchItem),
            });
            setResult(data);
        } catch (e: unknown) {
            setError(errMsg(e));
        }
    };

    return (
        <div className="space-y-5" data-page="blog-posts-import">
            <PageHeader
                title="Importar artículos"
                description="Sube .html, .md o un JSON de lote. Siempre quedan En revisión; nadie publica a ciegas."
            >
                <Button type="button" variant="outline" size="sm" onClick={() => navigate('/blog?tab=posts')}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Cola
                </Button>
            </PageHeader>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-card p-4 space-y-3">
                    <h2 className="font-semibold text-sm">1. Prepáralo con la IA</h2>
                    <p className="text-sm text-muted-foreground">
                        Copia el prompt, pégalo en Claude o ChatGPT, y pídele un .html por artículo o un JSON con varios posts.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => copy('prompt')}>
                            <Copy className="h-4 w-4 mr-1" />
                            {copied === 'prompt' ? 'Prompt copiado' : 'Copiar prompt'}
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => copy('tpl')}>
                            {copied === 'tpl' ? 'Plantilla copiada' : 'Copiar plantilla'}
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
                            Descargar plantilla.html
                        </Button>
                    </div>
                    <pre className="text-[11px] bg-muted/50 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">
                        {BLOG_AI_SYSTEM_PROMPT}
                    </pre>
                </div>
                <div className="rounded-xl border bg-card p-4 space-y-3">
                    <h2 className="font-semibold text-sm">2. Súbelo aquí</h2>
                    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer hover:bg-muted/30">
                        <FileUp className="h-6 w-6 text-muted-foreground" />
                        <span className="text-sm font-medium">Suelta o elige varios .html / .md / .json</span>
                        <input
                            type="file"
                            className="hidden"
                            accept=".md,.html,.json,.txt,text/markdown,text/html,application/json"
                            multiple
                            onChange={(e) => onFiles(e.target.files)}
                        />
                    </label>
                    <Textarea
                        rows={5}
                        placeholder="O pega aquí un .html o un JSON { posts: [...] }"
                        value={paste}
                        onChange={(e) => setPaste(e.target.value)}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={onPasteParse}>
                        Parsear texto pegado
                    </Button>
                </div>
            </div>

            {parsed.length > 0 && (
                <div className="rounded-xl border overflow-hidden">
                    <div className="px-3 py-2 bg-muted/40 text-sm flex justify-between">
                        <span>{parsed.length} archivos · {ready.length} listos · {blocked} con problema</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Archivo</th>
                                <th className="px-3 py-2">Slug</th>
                                <th className="px-3 py-2">Título</th>
                                <th className="px-3 py-2">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {parsed.map((p) => (
                                <tr key={p.filename} className="border-t">
                                    <td className="px-3 py-2">{p.filename}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{p.slug || '—'}</td>
                                    <td className="px-3 py-2 text-xs">{p.patch.title || '—'}</td>
                                    <td className="px-3 py-2">
                                        {p.error || p.leak ? (
                                            <span className="text-destructive text-xs">{p.error || p.warnings.join(' ')}</span>
                                        ) : (
                                            <span className="text-emerald-700 dark:text-emerald-400 text-xs">
                                                Listo{p.warnings.length ? ` · ${p.warnings.join(', ')}` : ''}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="p-3 flex flex-wrap gap-2 border-t">
                        <Button type="button" variant="outline" disabled={mut.isPending || ready.length === 0} onClick={() => run(true)}>
                            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            Dry-run (no guarda)
                        </Button>
                        <Button type="button" disabled={mut.isPending || ready.length === 0} onClick={() => run(false)}>
                            Aplicar a revisión
                        </Button>
                    </div>
                </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            {result && (
                <div className="rounded-xl border overflow-hidden">
                    <div className="px-3 py-2 bg-muted/40 text-sm">
                        {result.dryRun ? 'Simulación' : 'Aplicado'}: {result.applied} de {result.total}
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Slug</th>
                                <th className="px-3 py-2">Título</th>
                                <th className="px-3 py-2">Resultado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(result.items || []).map((row) => (
                                <tr key={`${row.index}-${row.slug}`} className="border-t">
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {row.id ? (
                                            <Link className="underline" to={`/blog/posts/${row.id}`}>
                                                {row.slug}
                                            </Link>
                                        ) : (
                                            row.slug || '—'
                                        )}
                                    </td>
                                    <td className="px-3 py-2">{row.title || '—'}</td>
                                    <td className={cn('px-3 py-2 text-xs', row.status === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : 'text-destructive')}>
                                        {row.status}{row.action ? ` · ${row.action}` : ''}{row.reason ? ` · ${row.reason}` : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
